import { DecimalPipe, UpperCasePipe } from "@angular/common";
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { HotDocComponent } from "@components/hot-doc/hot-doc.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { LabelComponent } from "@components/label/label.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import _, { cloneDeep, isNil } from "lodash";
import { Observable, Subject, asapScheduler } from "rxjs";
import {
    filter,
    map,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IHotColumnGroup } from "~app/common/components/hot-doc/hotdoc/doc";
import { ToastService } from "~app/common/components/toast/toast.service";
import { Produktgrupp, TradslagKortnamn } from "~app/common/models/constants";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { AtgardMediatorService } from "~app/features/virkeskop/services/atgard-mediator.service";
import { AvtalService } from "~app/features/virkeskop/services/avtal.service";
import { KontraktMediatorService } from "~app/features/virkeskop/services/kontrakt-mediator.service";
import { TradslagMediatorService } from "~app/features/virkeskop/services/tradslag-mediator.service";
import { UpdateSnabbkalkylmallCommand } from "../../../commands/tradslag/UpdateSnabbkalkylmallCommand";
import { VKSnabbkalkyl, VKSnabbkalkylTradslagDTO, VKSnabbkalkylUtbyteRequestDTO } from "../../../models/tradslag/snabbkalkyl-models";
import { VKForkalkylTradslagDTO, isNotGran, isNotGranOrTall } from "../../../models/tradslag/tradslag-models";
import { ForkalkylSnabbkalkylClientValidationService } from "../../../services/validation/forkalkyl-snabbkalkyl-client-validation.service";
import { VirkeskopValidationIconPopoverComponent } from "../../../validation/validation-icon-popover/virkeskop-validation-icon-popover.component";
import { AddTradslagButtonComponent } from "../add-tradslag-button/add-tradslag-button.component";
import { TradslagUtbyteComponent } from "../tradslag-utbyte/tradslag-utbyte.component";
import { TradslagColumnDefinition, TradslagTableBinder } from "../utilities/tradslag-table-binder";

export type SnabbkalkylColumnDefinition = TradslagColumnDefinition<VKSnabbkalkylTradslagDTO>
    & { cellName: keyof Omit<VKSnabbkalkylTradslagDTO, "tradslag" | "guid"> };

export const meanRoundedWeighted = (_values: number[], tableData: VKSnabbkalkylTradslagDTO[]) => {
    let total = 0;
    tableData.forEach((item) => {
        total += item.andel * item.medelstam;
    });
    return _.round(total / 100.0, 2);
};
export const validateAsDecimal = (value: any) => {
    if (_.isNaN(value)) {
        return false;
    }

    const decimal = Number.parseFloat(value);
    return _.isNumber(decimal) && decimal >= 0;
};

const timmerGroup: IHotColumnGroup = {
    label: "Procent av timmer"
};

export const snabbkalkylColumns: SnabbkalkylColumnDefinition[] = [
    {
        header: "Andel",
        cellName:"andel",
        readOnly: false,
        type: "numeric",
        sumFunction: _.sum,
        tooltip:"Andel trädslag i %"
    },
    {
        header: "mstam",
        cellName:"medelstam",
        readOnly: false,
        type: "numeric",
        sumFunction: meanRoundedWeighted,
        tooltip:"Medelstam m3f/träd - på uttaget*",
        validateFn: validateAsDecimal
    },
    {
        header: "Timmer",
        cellName:"timmerandel",
        readOnly: false,
        type: "numeric",
        tooltip:"Timmerandel %"
    },
    {
        header: "Kubb/klenT",
        cellName:"varavKlentimmer",
        readOnly: false,
        type: "numeric",
        group: timmerGroup,
        width: 90,
        tooltip:"Varav andel Kubb/klentimmer i %",
        disabled: isNotGranOrTall
    },
    {
        header: "Stamblock",
        cellName:"varavGrovtimmer",
        readOnly: false,
        type: "numeric",
        group: timmerGroup,
        width: 90,
        tooltip:"Varav andel grovt i %",
        disabled: isNotGranOrTall
    },
    {
        header: "MassaV",
        cellName:"massavedandel",
        readOnly: false ,
        type: "numeric",
        tooltip:"Andel massaved i %"
    },
    {
        header: "GmaV",
        cellName:"granmassavedsandel",
        readOnly: false ,
        type: "numeric",
        tooltip:"Andel granmassaved i %",
        disabled: isNotGran
    },
    {
        header: "BränsleV",
        cellName:"branslevedandel",
        readOnly: false,
        type: "numeric",
        tooltip:"BränsleVed i %"
    }
];

@Component({
    selector: "rask-forkalkyl-snabbkalkyl",
    templateUrl: "./forkalkyl-snabbkalkyl.component.html",
    styleUrls: ["./forkalkyl-snabbkalkyl.component.scss"],
    providers: [ForkalkylSnabbkalkylClientValidationService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        TextmultilineComponent,
        InputNumberComponent,
        FormsModule,
        HotDocComponent,
        AddTradslagButtonComponent,
        ButtonComponent,
        VirkeskopValidationIconPopoverComponent,
        LabelComponent,
        TradslagUtbyteComponent,
        UpperCasePipe,
        DecimalPipe
    ]
})
export class ForkalkylSnabbkalkylComponent implements OnInit, OnDestroy {
    @Input() readonly = false;

    protected m3skVisible = false;
    protected model: VKSnabbkalkyl;

    private readonly atgardMediatorService = inject(AtgardMediatorService);
    private readonly avtalService = inject(AvtalService);
    private readonly binder = new TradslagTableBinder<VKSnabbkalkylTradslagDTO>(snabbkalkylColumns);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly forkalkylSnabbkalkylClientValidationService = inject(ForkalkylSnabbkalkylClientValidationService);
    private readonly infoIconProduktgrupper: string[]  = [
        "Talltimmer",
        "Grantimmer"
    ];
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly konverteringsFaktorM3fToM3sk = 1.20;
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly toastService = inject(ToastService);
    private readonly tradslagMediatorService = inject(TradslagMediatorService);

    private anyValidationErrors = true;
    private isDirty = false;
    private templateModel: VKSnabbkalkyl;

    get selectedTradslag() {
        return (this.model?.snabbkalkylTradslag ?? [])
            .map(t => t.tradslag);
    }

    protected get atgard() {
        return this.atgardMediatorService.atgard;
    }

    protected get hotDoc() {
        return this.binder.hotDoc;
    }

    protected get htIdentifier() {
        return `snabbkalkylHT/${this.atgard.guid}`;
    }

    protected get totalVolymM3f() {
        const volymM3f = (this.model.uttagM3FubHa ?? 0) * (this.atgard.nettoareal ?? 0);
        return _.round(volymM3f, 0);
    }

    protected get totalVolymM3sk() {
        const volymM3f = (this.model.uttagM3FubHa ?? 0) * (this.atgard.nettoareal ?? 0);
        return _.round(volymM3f * this.konverteringsFaktorM3fToM3sk, 0);
    }

    protected get utbyteTimmerAndel() {
        return this.model.utbyte?.timmerandel;
    }

    private get hasChanges() {
        return _.isNil(this.model.beraknadTid) || this.isDirty;
    }

    ngOnDestroy(): void {
        asapScheduler.schedule(() =>
            this.forkalkylSnabbkalkylClientValidationService.clearValidations()
        );
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit(): void {
        this.binder.modelChange$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.handleModelChange());

        this.tradslagMediatorService.loaded$
            .pipe(takeUntil(this.ngUnsubscribe$), filter(loaded => loaded === true))
            .subscribe(isLoaded => {
                this.model = this.tradslagMediatorService.snabbkalkyl;
                this.binder.initTable(this.model.snabbkalkylTradslag);

                this.tradslagMediatorService.onMallSelected$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe(([
                        snabbkalkylmall,
                        direktutbytemall
                    ]) => {
                        this.templateModel = cloneDeep(snabbkalkylmall);
                        this.applyModel(snabbkalkylmall);
                    });

                this.tradslagMediatorService.onSaveMall$
                    .pipe(
                        switchMap(mallnamn => this.busyService.addWork(this.handleSaveMall(mallnamn))),
                        tap(mallnamn => {
                            this.toastService.add(`Snabbkalkylmallen '${mallnamn}' sparades`);
                            this.tradslagMediatorService.onMallChanged.next();
                        }),
                        takeUntil(this.ngUnsubscribe$))
                    .subscribe();

                if(this.model.snabbkalkylTradslag?.length > 0) {
                    asapScheduler.schedule(() =>
                        this.validateModel()
                    );
                }

                this.kontraktMediatorService.kontrakt$
                    .pipe(takeUntil(this.ngUnsubscribe$))
                    .subscribe((payload) => {
                        if (payload !== null) {
                            const cleared = isNil(this.templateModel);
                            if (!cleared) {
                                this.templateModel = null;
                                this.handleModelChange();
                            }
                        }
                    });
            });
    }

    protected calculateSnabbkalkyl(): void {
        if (this.readonly) {
            return;
        }
        const request: VKSnabbkalkylUtbyteRequestDTO = {
            uttagM3FubHa: this.model.uttagM3FubHa,
            tradslag: this.model.snabbkalkylTradslag
        };

        this.busyService.addWork(this.avtalService.calculateSnabbkalkylUtbyte(request))
            .subscribe({
                next: beraknatUtbyte => {
                    this.model.utbyte = beraknatUtbyte;
                    this.model.beraknadTid = DateUtils.now();
                    this.handleModelChange(true);
                    this.isDirty = false;
                },
                error: () => {
                //
                }
            });
    }

    protected getBeraknadTid(): string {
        return DateUtils.formatDateString(this.model?.beraknadTid, DateUtils.datetimeformatWithoutMillis);
    }

    // skickas ner till utbyteskomponenten, och bör därför vara en arrow function
    protected getProduktgruppVolym = (volym: number) => {
        // summan multipliceras med åtgärdens nettoareal i frontend
        const summa = volym * (this.atgard.nettoareal ?? 0);
        return _.round(summa, 0);
    };

    // skickas också ner till utbyteskomponenten
    protected getProduktgruppVolymStamblock = (produktgruppNamn: string): number => {
        if (this.model.snabbkalkylTradslag.length === 0) {
            return 0;
        }

        let tradslag: VKSnabbkalkylTradslagDTO;
        if (produktgruppNamn === Produktgrupp.Talltimmer) {
            tradslag = this.model.snabbkalkylTradslag.find(x => x.tradslag.namn === TradslagKortnamn.Tall);
        } else if (produktgruppNamn === Produktgrupp.Grantimmer) {
            tradslag = this.model.snabbkalkylTradslag.find(x => x.tradslag.namn === TradslagKortnamn.Gran);
        }
        if (!tradslag) {
            return 0;
        }

        const stamblock = (this.atgard.nettoareal * this.model.uttagM3FubHa) * (tradslag.andel / 100) * (tradslag.timmerandel / 100) * (tradslag.varavGrovtimmer / 100);
        return _.round(stamblock, 0);
    };

    protected onTradslagAdded(tradslag: VKForkalkylTradslagDTO) {
        // lägg till rad
        this.model.snabbkalkylTradslag.push({
            andel: 0,
            medelstam: 0,
            timmerandel: 0,
            massavedandel: 0,
            branslevedandel: 0,
            granmassavedsandel: 0,
            varavGrovtimmer: 0,
            varavKlentimmer: 0,
            tradslag
        });

        this.handleModelChange();
        this.binder.rebind();
    }

    protected onUttagChanged() {
        this.validateModel();
        this.handleChange();
    }

    protected showCalculate(): boolean {
        return this.model.snabbkalkylTradslag?.length > 0
            && !this.anyValidationErrors
            && this.model.uttagM3FubHa > 0
            && this.hasChanges;
    }

    protected showInfoIcon(namn: string): boolean {
        return this.infoIconProduktgrupper.includes(namn);
    }

    protected showM3sk(): void {
        this.m3skVisible = true;
    }

    private applyModel(model: VKSnabbkalkyl) {
        this.model = model;
        this.binder.initTable(this.model.snabbkalkylTradslag);

        if (this.model.snabbkalkylTradslag?.length > 0) {
            asapScheduler.schedule(() =>
                this.handleModelChange()
            );
        }
    }

    private handleChange(isCalculate = false) {
        this.isDirty = true;
        this.tradslagMediatorService.updateSnabbkalkyl(this.atgard.guid, this.model, isCalculate);
    }

    private handleModelChange(isCalculate = false) {
        this.validateModel();
        this.tradslagMediatorService.recalculateMedelstam();

        this.handleChange(isCalculate);
    }

    private handleSaveMall(mallNamn: string): Observable<string> {
        const snabbkalkylmall = this.model.toMallDto(mallNamn);

        const command = new UpdateSnabbkalkylmallCommand(this.atgard.guid, snabbkalkylmall);

        this.commandService.addTransactionCommand(command);
        return this.commandService.pushCommandQueueTransaction().pipe(
            tap(() => this.commandService.reset()),
            map(() => mallNamn)
        );
    }

    private validateModel() {
        let cellStyles;
        [
            cellStyles,
            this.anyValidationErrors
        ] = this.forkalkylSnabbkalkylClientValidationService.updateValidationTableFields(this.templateModel);

        this.binder.setCellStyles(cellStyles);
    }
}
